"use client";

import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import {alpha, useTheme} from "@mui/material/styles";

import FaIcon from "@/components/utilities/fa-icon";
import {useProductsFilters} from "@/hooks/useProductsFilters";

interface PaginationProps {
    numberOfPages: number;
    currentPage: number;
}

export default function SwrPagination({
                                          numberOfPages,
                                          currentPage,
                                      }: PaginationProps): JSX.Element | null {
    const theme = useTheme();

    const {updateFilters} = useProductsFilters();

    const [inputText, setInputText] = useState(
        currentPage.toString()
    );

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    const shouldRender = numberOfPages > 1;

    const normalizePage = useCallback(
        (value: string | number): number => {
            const parsedPage = Number(value);

            if (!Number.isFinite(parsedPage)) {
                return currentPage;
            }

            return Math.min(
                Math.max(Math.trunc(parsedPage), 1),
                numberOfPages
            );
        },
        [currentPage, numberOfPages]
    );

    const setPage = useCallback(
        (page: number) => {
            const normalizedPage = normalizePage(page);

            setInputText(normalizedPage.toString());

            if (normalizedPage === currentPage) {
                return;
            }

            updateFilters({
                Page: normalizedPage,
            });
        },
        [currentPage, normalizePage, updateFilters]
    );

    const applyInputValue = useCallback(() => {
        if (inputText.trim() === "") {
            setInputText(currentPage.toString());
            return;
        }

        setPage(normalizePage(inputText));
    }, [
        currentPage,
        inputText,
        normalizePage,
        setPage,
    ]);

    useEffect(() => {
        setInputText(currentPage.toString());
    }, [currentPage]);

    useEffect(() => {
        if (!shouldRender || inputText.trim() === "") {
            return;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const parsedPage = Number(inputText);

        if (
            !Number.isFinite(parsedPage) ||
            Math.trunc(parsedPage) === currentPage
        ) {
            return;
        }

        timerRef.current = setTimeout(() => {
            setPage(normalizePage(inputText));
        }, 800);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [
        currentPage,
        inputText,
        normalizePage,
        setPage,
        shouldRender,
    ]);

    const handleSubmit = (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        applyInputValue();
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <form onSubmit={handleSubmit}>
            <Stack
                pt={5}
                alignItems="center"
                justifyContent="center"
                direction={{xs: "column", md: "row"}}
                spacing={1}
            >
                <Stack
                    spacing={1}
                    direction="row-reverse"
                    sx={{
                        "& .MuiButton-root": {
                            minWidth: 50,
                            height: 50,
                        },
                    }}
                >
                    <Button
                        type="button"
                        disabled={currentPage <= 1}
                        color="primary"
                        variant="outlined"
                        onClick={() => setPage(1)}
                        sx={{
                            fontSize: 18,
                        }}
                    >
                        1
                    </Button>

                    <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={currentPage <= 1}
                        onClick={() => setPage(currentPage - 1)}
                    >
                        <FaIcon faIcon="fa-arrow-left"/>
                    </Button>

                    <InputBase
                        dir="ltr"
                        type="number"
                        value={inputText}
                        inputProps={{
                            inputMode: "numeric",
                            "aria-label": "شماره صفحه",
                            sx: {
                                px: 1,
                                textAlign: "center",
                                fontWeight: 400,
                            },
                        }}
                        onChange={(event) => {
                            setInputText(event.target.value);
                        }}
                        onFocus={() => {
                            if (inputText === currentPage.toString()) {
                                setInputText("");
                            }
                        }}
                        onBlur={applyInputValue}
                        sx={{
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                0.1
                            ),
                            border: `1px solid ${theme.palette.primary.main}`,
                            outline: 0,
                            borderRadius: theme.shape.borderRadius,
                            fontSize: 18,
                            color: `${theme.palette.primary.main} !important`,
                            fontWeight: 500,
                            height: 50,
                            width: 70,
                            minWidth: 50,
                            flexShrink: 0,
                            boxSizing: "border-box",
                        }}
                    />

                    <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={currentPage >= numberOfPages}
                        onClick={() => setPage(currentPage + 1)}
                    >
                        <FaIcon faIcon="fa-arrow-right"/>
                    </Button>

                    <Button
                        type="button"
                        disabled={currentPage >= numberOfPages}
                        color="primary"
                        variant="outlined"
                        onClick={() => setPage(numberOfPages)}
                        sx={{
                            fontSize: 18,
                        }}
                    >
                        {numberOfPages}
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
}
